import React from 'react';

function MiniChart({ data, dataKey, color, height = 120, showLabels = false, showGrid = false }) {
  if (!data || data.length === 0) return null;

  const values = data.map((d) => d[dataKey]);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const w = 100;
  const h = height;
  const padding = 4;

  const points = values.map((v, i) => {
    const x = padding + (i / (values.length - 1)) * (w - padding * 2);
    const y = h - padding - ((v - min) / range) * (h - padding * 2);
    return `${x},${y}`;
  }).join(' ');

  const areaPoints = `${padding},${h - padding} ${points} ${w - padding},${h - padding}`;

  const gridLines = showGrid ? [0.25, 0.5, 0.75].map((pct) => h - padding - pct * (h - padding * 2)) : [];

  return (
    <div className="mini-chart-container">
      <svg viewBox={`0 0 ${w} ${h}`} className="mini-chart" preserveAspectRatio="none">
        {gridLines.map((y, i) => (
          <line key={i} x1={padding} y1={y} x2={w - padding} y2={y}
            stroke="var(--border)" strokeWidth="0.3" strokeDasharray="2,2" />
        ))}
        <defs>
          <linearGradient id={`grad-${dataKey}-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <polygon
          points={areaPoints}
          fill={`url(#grad-${dataKey}-${color.replace('#', '')})`}
        />
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {showLabels && data.length > 0 && (
        <div className="mini-chart-labels">
          <span>{data[0].date}</span>
          <span>{data[data.length - 1].date}</span>
        </div>
      )}
    </div>
  );
}

export function BarChart({ data, dataKey, color, height = 120 }) {
  if (!data || data.length === 0) return null;
  const values = data.map((d) => d[dataKey]);
  const max = Math.max(...values);

  return (
    <div className="bar-chart" style={{ height }}>
      {data.map((d, i) => (
        <div key={i} className="bar-chart-col" title={`${d.date}: ${d[dataKey]}`}>
          <div
            className="bar-chart-bar"
            style={{
              height: `${(d[dataKey] / max) * 100}%`,
              background: color,
            }}
          ></div>
        </div>
      ))}
    </div>
  );
}

export function DonutChart({ segments, size = 120 }) {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  const radius = 40;
  const cx = 50;
  const cy = 50;
  let currentAngle = -90;

  return (
    <svg viewBox="0 0 100 100" width={size} height={size} className="donut-chart">
      {segments.map((seg, i) => {
        const angle = (seg.value / total) * 360;
        const startAngle = currentAngle;
        const endAngle = currentAngle + angle;
        currentAngle = endAngle;

        const startRad = (startAngle * Math.PI) / 180;
        const endRad = (endAngle * Math.PI) / 180;
        const x1 = cx + radius * Math.cos(startRad);
        const y1 = cy + radius * Math.sin(startRad);
        const x2 = cx + radius * Math.cos(endRad);
        const y2 = cy + radius * Math.sin(endRad);
        const largeArc = angle > 180 ? 1 : 0;

        return (
          <path
            key={i}
            d={`M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`}
            fill={seg.color}
            stroke="var(--bg-card)"
            strokeWidth="1"
          />
        );
      })}
      <circle cx={cx} cy={cy} r="25" fill="var(--bg-card)" />
    </svg>
  );
}

export default MiniChart;
